import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Home, Mail, Phone, MapPin, Send, CheckCircle, MessageCircle, Menu, X } from 'lucide-react';
import { toast } from 'sonner';
import EdpiesLogo from '@/components/EdpiesLogo';

const fadeInUp = { hidden: { opacity: 0, y: 40 }, visible: { opacity: 1, y: 0 } };

const Contact = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navItems = [
    { label: 'About Us', path: '/about' },
    { label: 'Resources', path: '/resources' },
    { label: 'Contact', path: '/contact' },
    { label: 'FAQ', path: '/faq' }
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    // Simulate form submission
    setSubmitted(true);
    toast.success('Message sent successfully!');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-xl border-b border-slate-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
              <EdpiesLogo size="sm" />
              <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-purple-400 to-indigo-400 bg-clip-text text-transparent">Edpies</span>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-4">
              {navItems.map((item) => (
                <Button 
                  key={item.path}
                  variant="ghost" 
                  onClick={() => navigate(item.path)}
                  className={`text-slate-300 hover:text-white hover:bg-slate-800 ${item.path === '/contact' ? 'text-purple-400' : ''}`}
                >
                  {item.label}
                </Button>
              ))}
              <Button 
                variant="ghost" 
                onClick={() => navigate('/auth')}
                className="text-slate-300 hover:text-white hover:bg-slate-800"
              >
                Sign In
              </Button>
              <Button onClick={() => navigate('/auth')} className="bg-gradient-to-r from-purple-600 to-indigo-600">
                Get Started
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center gap-2">
              <Button 
                size="sm"
                onClick={() => navigate('/auth')}
                className="bg-gradient-to-r from-purple-600 to-indigo-600 text-xs px-3"
              >
                Sign In
              </Button>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-slate-300 hover:text-white p-2"
              >
                {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <motion.div 
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="md:hidden border-t border-slate-800/50 py-3"
            >
              <div className="flex flex-wrap gap-2">
                {navItems.map((item) => (
                  <Button 
                    key={item.path}
                    variant="ghost" 
                    size="sm"
                    onClick={() => {
                      navigate(item.path);
                      setMobileMenuOpen(false);
                    }}
                    className={`text-slate-300 hover:text-white ${item.path === '/contact' ? 'text-purple-400' : ''}`}
                  >
                    {item.label}
                  </Button>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-5xl md:text-6xl font-bold text-white mb-6" style={{ fontFamily: 'Space Grotesk' }}>
            Get in <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">Touch</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="text-xl text-slate-400">
            We'd love to hear from you. Send us a message and we'll respond as soon as possible.
          </motion.p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 px-4">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-12">
          {/* Contact Info */}
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}>
            <h2 className="text-2xl font-bold text-white mb-6">Contact Information</h2>
            <div className="space-y-6">
              {[
                { icon: Mail, label: 'Email', value: 'info@edpies.com', href: 'mailto:info@edpies.com' },
                { icon: Phone, label: 'Phone', value: '+44 (0) 7555 443736', href: 'tel:+447555443736' },
                { icon: MessageCircle, label: 'WhatsApp', value: 'Send us a message', href: 'https://wa.me/447555443736?text=Hi%20Edpies%2C%20I%20would%20like%20to%20know%20more%20about%20your%20services.', isWhatsApp: true },
                { icon: MapPin, label: 'Address', value: 'London, United Kingdom' }
              ].map((item, idx) => (
                <div key={idx} className="flex items-start gap-4">
                  <div className={`${item.isWhatsApp ? 'bg-green-500/10' : 'bg-purple-500/10'} p-3 rounded-xl`}>
                    <item.icon className={`h-5 w-5 ${item.isWhatsApp ? 'text-green-400' : 'text-purple-400'}`} />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">{item.label}</p>
                    {item.href ? (
                      <a 
                        href={item.href} 
                        target={item.isWhatsApp ? '_blank' : undefined}
                        rel={item.isWhatsApp ? 'noopener noreferrer' : undefined}
                        className={`${item.isWhatsApp ? 'text-green-400 hover:text-green-300' : 'text-white hover:text-purple-300'} transition-colors`}
                      >
                        {item.value}
                      </a>
                    ) : (
                      <p className="text-white">{item.value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* WhatsApp CTA Button */}
            <a 
              href="https://wa.me/447555443736?text=Hi%20Edpies%2C%20I%20would%20like%20to%20know%20more%20about%20your%20services."
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 flex items-center justify-center gap-2 w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-xl transition-colors"
            >
              <MessageCircle className="h-5 w-5" />
              Chat with us on WhatsApp
            </a>

            <div className="mt-10 p-6 bg-slate-800/50 rounded-xl border border-slate-700/50">
              <h3 className="text-lg font-semibold text-white mb-2">Support Hours</h3>
              <p className="text-slate-400 text-sm">Monday - Friday: 9:00 AM - 6:00 PM GMT</p>
              <p className="text-slate-400 text-sm">Saturday: 10:00 AM - 2:00 PM GMT</p>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}>
            <Card className="bg-slate-800/50 border-slate-700/50">
              <CardContent className="p-6">
                {submitted ? (
                  <div className="text-center py-12">
                    <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring" }}>
                      <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                    </motion.div>
                    <h3 className="text-xl font-bold text-white mb-2">Message Sent!</h3>
                    <p className="text-slate-400 mb-6">We'll get back to you within 24 hours.</p>
                    <Button onClick={() => setSubmitted(false)} variant="outline">Send Another Message</Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <Label className="text-slate-300">Name</Label>
                      <Input placeholder="Your name" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required className="bg-slate-900/50 border-slate-600 text-white mt-1" />
                    </div>
                    <div>
                      <Label className="text-slate-300">Email</Label>
                      <Input type="email" placeholder="your@email.com" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required className="bg-slate-900/50 border-slate-600 text-white mt-1" />
                    </div>
                    <div>
                      <Label className="text-slate-300">Subject</Label>
                      <Input placeholder="How can we help?" value={formData.subject} onChange={(e) => setFormData({...formData, subject: e.target.value})} required className="bg-slate-900/50 border-slate-600 text-white mt-1" />
                    </div>
                    <div>
                      <Label className="text-slate-300">Message</Label>
                      <Textarea placeholder="Your message..." rows={5} value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} required className="bg-slate-900/50 border-slate-600 text-white mt-1 resize-none" />
                    </div>
                    <Button type="submit" className="w-full h-12 bg-gradient-to-r from-purple-600 to-indigo-600">
                      <Send className="h-4 w-4 mr-2" /> Send Message
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-slate-800 mt-12">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <p className="text-slate-500 text-sm">© 2025 Edpies</p>
          <div className="flex gap-4">
            <Button variant="ghost" size="sm" className="text-slate-400" onClick={() => navigate('/')}>Home</Button>
            <Button variant="ghost" size="sm" className="text-slate-400" onClick={() => navigate('/about')}>About</Button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Contact;
